import { PayloadAction, createSlice, original } from '@reduxjs/toolkit';
import isEqual from 'lodash-es/isEqual';

import {
  AllViewersPayload,
  Presence,
  PresenceState,
  PresenceStatus,
  RemoveViewerPayload,
  ViewerPayload,
} from '@/shared/types/presence';
import { AgentActivity, AgentId } from '@/shared/types/rtm';
import { getSecondsDifference } from '@/util/dateUtils';

const initialState: PresenceState = Object.freeze({
  brains: {},
  collections: {},
  datasources: {},
  desks: {},
  guidelines: {},
  sessions: {},
  agents: [],
});

const presenceSlice = createSlice({
  name: 'presence',
  initialState: { ...initialState },
  reducers: {
    setAllViewers: (state, action: PayloadAction<AllViewersPayload>) => {
      const { type, resources } = action.payload;
      Object.keys(resources).forEach((id) => {
        const currentViewers = state[type][id];
        const newViewers = resources[id];

        // Check if viewers have actually changed
        if (currentViewers && currentViewers.length === newViewers.length) {
          const hasChanged = newViewers.some((viewer, index) => {
            const current = currentViewers[index];
            return (
              !current ||
              current.user_id !== viewer.user_id ||
              current.timestamp !== viewer.timestamp
            );
          });

          if (!hasChanged) {
            return; // No changes, avoid creating new array
          }
        }

        // Only create new array if data has changed
        state[type][id] = [...newViewers].sort((a, b) => a.timestamp - b.timestamp);
      });
    },

    setViewers: (state, action: PayloadAction<ViewerPayload>) => {
      const { type, id, agents } = action.payload;
      const currentViewers = state[type][id];

      // Check if viewers have actually changed
      if (currentViewers && currentViewers.length === agents.length) {
        const hasChanged = agents.some((agent, index) => {
          const current = currentViewers[index];
          return (
            !current || current.user_id !== agent.user_id || current.timestamp !== agent.timestamp
          );
        });

        if (!hasChanged) {
          return; // No changes, avoid creating new array
        }
      }

      // Only create new array if data has changed
      state[type][id] = [...agents].sort((a, b) => a.timestamp - b.timestamp);
    },
    removeViewer: (state, action: PayloadAction<RemoveViewerPayload>) => {
      const { type, id, user_id } = action.payload;
      if (!state[type][id]) {
        return;
      }
      state[type][id] = state[type][id]?.filter((a: AgentActivity) => a.user_id !== user_id);
    },
    setAgents: (state, action: PayloadAction<{ agents: Presence[] }>) => {
      // The whole roster arrives on every event; replacing it re-renders every `useAccount()` consumer.
      if (!isEqual(original(state.agents) ?? state.agents, action.payload.agents)) {
        state.agents = action.payload.agents;
      }
    },

    setAgentsFromPresence: (state, action: PayloadAction<{ users: Presence[] }>) => {
      // Only update if data has actually changed
      if (!isEqual(state.agents, action.payload.users)) {
        state.agents = action.payload.users;
      }
    },
    setAgentStatus: (
      state,
      action: PayloadAction<{ agent_id: string; status: PresenceStatus }>
    ) => {
      const { agent_id, status } = action.payload;
      const agent = state.agents.find((a) => a.agent_id === agent_id);
      if (agent && agent.status !== status) {
        agent.status = status;
      }
    },
    markAgentOnline: (state, action: PayloadAction<AgentId>) => {
      const agent = state.agents.find((a) => a.agent_id === action.payload.user_id);
      if (agent && !agent.is_connected) {
        agent.is_connected = true;
      }
    },
    markAgentOffline: (state, action: PayloadAction<AgentId>) => {
      const agent = state.agents.find((a) => a.agent_id === action.payload.user_id);
      if (agent && agent.is_connected) {
        agent.is_connected = false;
      }
    },
    updateAgentActivity: (state, action: PayloadAction<AgentActivity>) => {
      const agentIndex = state.agents.findIndex((a) => a.agent_id === action.payload.user_id);
      if (agentIndex !== -1) {
        const newTimestamp = new Date(action.payload.timestamp).toISOString();
        // Only update if the timestamp has actually changed significantly (more than 10 seconds)
        const currentTimestamp = state.agents[agentIndex].last_activity_at;
        if (
          !currentTimestamp ||
          Math.abs(getSecondsDifference(newTimestamp, currentTimestamp)) > 10
        ) {
          state.agents[agentIndex].last_activity_at = newTimestamp;
        }
      }
    },
  },
});

export const {
  setViewers,
  setAllViewers,
  removeViewer,
  setAgents,
  setAgentsFromPresence,
  markAgentOffline,
  markAgentOnline,
  updateAgentActivity,
  setAgentStatus,
} = presenceSlice.actions;

export default presenceSlice.reducer;
